import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/apiService';
import { formatDateTime } from '../utils/dateFormatter';

interface AdminSearchRow {
  id: number;
  user_email: string;
  parameter_type_name: string;
  search_value: string;
  country_code?: string | null;
  status: string;
  created_at: string;
}

export const AdminAllSearchesReportPage: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<AdminSearchRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.adminGetAllSearches({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: status || undefined,
        ordering: '-created_at'
      });
      setRows((data.results as unknown as AdminSearchRow[]) || []);
      setCount(data.count || 0);
    } catch (err) {
      console.error('Failed to load admin searches:', err);
      showToast('Failed to load searches', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, status, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / pageSize)), [count, pageSize]);

  const formatPhoneNumber = (countryCode?: string | null, value?: string): string => {
    if (!countryCode || !value) return value || '';
    const clean = countryCode.startsWith('+') ? countryCode.slice(1) : countryCode;
    return `+${clean} ${value}`;
  };

  const handleRefund = async (id: number) => {
    try {
      await apiService.adminRefundSearchIntCoins(id);
      showToast('IntCoins refunded successfully', 'success');
      loadData();
    } catch (err) {
      console.error('Refund failed:', err);
      showToast('Failed to refund IntCoins', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="All Searches Report"
        subtitle="View all users' searches, open reports, and process IntCoins refunds"
        align="center"
      />

      <Card>
        <div className="p-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Search</label>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by email or query"
                className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm w-64 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Status</label>
              <select
                value={status}
                onChange={e => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setSearch(''); setStatus(''); setPage(1); }}>Reset</Button>
            <Button onClick={() => { setPage(1); loadData(); }}>Apply</Button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-0 overflow-x-auto custom-scrollbar">
          <table className="min-w-full text-sm text-gray-700 dark:text-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left font-medium">#</th>
                <th className="px-4 py-3 text-left font-medium">User Email</th>
                <th className="px-4 py-3 text-left font-medium">Parameter</th>
                <th className="px-4 py-3 text-left font-medium">Query</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">No searches found</td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}>
                    <td className="px-4 py-3">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="px-4 py-3">{row.user_email}</td>
                    <td className="px-4 py-3">{row.parameter_type_name}</td>
                    <td className="px-4 py-3">
                      {row.parameter_type_name === 'Phone Number' && row.country_code
                        ? formatPhoneNumber(row.country_code, row.search_value)
                        : row.search_value}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        row.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800' :
                        row.status === 'processing' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800' :
                        row.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-800' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatDateTime(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <a
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 underline"
                          href={`/app/search/${row.id}/report`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Report
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRefund(row.id)}
                          disabled={row.status === 'processing'}
                        >
                          Refund IntCoins
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {count > pageSize && (
          <div className="p-4 flex justify-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={page === 1}>First</Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <span className="px-2 py-1 text-sm text-gray-600 dark:text-gray-300">Page {page} of {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last</Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminAllSearchesReportPage;

